import React, { useEffect, useState } from "react";
import "./ShowAllVoxes.css";
import { serverService } from "../../_services/server.service";

type VoxState = "OK" | "DEGRADED" | "KO" | "WARNING" | "INACTIVE";

interface VoxError {
  type: string;
  message: string;
  severity: "critical" | "warning" | "info";
}

interface Vox {
  IdVox: number;
  IdCompany: number;
  Name: string;
  CompanyName: string;
  CompanyAutoPilot: boolean;
  LocationName: string;
  LastTimeAlive: string;
  MinutesSinceLastAlive: number;
  CurrentQuestion: string | null;
  LastQuestion: string | null;
  LastYesPercent: number | null;
  Battery: number | null;
  Temperature: number | null;
  BrightnessPercent: number | null;
  NextQuestions: number | null;
  IsCharging: boolean;
  IsFakeCharging: boolean;
  PedalsConnected: boolean;
  MemoryMB: number | null;
  Version: string | null;
  Screen: string;
  IsActive: boolean;
  IsNight: boolean;
  FirstUseDate: string | null;
  UpcomingSchedules: number;
  State: VoxState;
  Errors: VoxError[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const STATE_ORDER: Record<VoxState, number> = { KO: 0, DEGRADED: 1, WARNING: 2, OK: 3, INACTIVE: 4 };

function formatMinutes(min: number): string {
  if (min < 60) return `${min}m`;
  if (min < 1440) return `${Math.floor(min / 60)}h${min % 60 > 0 ? ` ${min % 60}m` : ""}`;
  return `${Math.floor(min / 1440)}j`;
}

function StateBadge({ state }: { state: VoxState }) {
  return <span className={`vd-badge vd-badge--${state.toLowerCase()}`}>{state}</span>;
}

function BatteryIcon({ pct, charging }: { pct: number | null; charging: boolean }) {
  if (pct === null) return <span className="vd-muted">—</span>;
  const cls = pct < 20 ? "vd-battery--low" : pct < 50 ? "vd-battery--mid" : "vd-battery--ok";
  return (
    <span className={`vd-battery ${cls}`}>
      {charging ? "⚡" : "🔋"} {pct}%
    </span>
  );
}

// ─── Desktop row ────────────────────────────────────────────────────────────

function VoxRow({ vox }: { vox: Vox }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className={`vd-row vd-row--${vox.State.toLowerCase()} ${expanded ? "vd-row--expanded" : ""}`}
        onClick={() => setExpanded(!expanded)}
      >
        <td><StateBadge state={vox.State} /></td>
        <td>
          <span className="vd-name">{vox.Name}</span>
          <br />
          <span className="vd-company">{vox.CompanyName}</span>
        </td>
        <td className="vd-muted">{vox.LocationName || "—"}</td>
        <td className={vox.MinutesSinceLastAlive > 60 ? "vd-alert" : ""}>
          {formatMinutes(vox.MinutesSinceLastAlive)}
        </td>
        <td><BatteryIcon pct={vox.Battery} charging={vox.IsCharging} /></td>
        <td>
          <span className={`vd-screen vd-screen--${vox.Screen}`}>{vox.Screen}</span>
        </td>
        <td className="vd-muted">{vox.Version || "—"}</td>
        <td>
          {vox.Errors.length === 0 ? (
            <span className="vd-muted">—</span>
          ) : (
            vox.Errors.map((e, i) => (
              <span key={i} className={`vd-errtag vd-errtag--${e.severity}`}>{e.type}</span>
            ))
          )}
        </td>
        <td className="vd-expand-icon">{expanded ? "▲" : "▼"}</td>
      </tr>
      {expanded && (
        <tr className="vd-detail-row">
          <td colSpan={9}>
            <div className="vd-detail">
              <div className="vd-detail__grid">
                <div className="vd-detail__item">
                  <span className="vd-detail__label">Température</span>
                  <span>{vox.Temperature !== null ? `${vox.Temperature}°C` : "—"}</span>
                </div>
                <div className="vd-detail__item">
                  <span className="vd-detail__label">Luminosité</span>
                  <span>{vox.BrightnessPercent !== null ? `${vox.BrightnessPercent}%` : "—"}</span>
                </div>
                <div className="vd-detail__item">
                  <span className="vd-detail__label">Mémoire</span>
                  <span>{vox.MemoryMB !== null ? `${vox.MemoryMB} MB` : "—"}</span>
                </div>
                <div className="vd-detail__item">
                  <span className="vd-detail__label">Pédales</span>
                  <span>{vox.PedalsConnected ? "✓ Connectées" : "✗ Manquantes"}</span>
                </div>
                <div className="vd-detail__item">
                  <span className="vd-detail__label">AutoPilot</span>
                  <span>{vox.CompanyAutoPilot ? "Oui" : "Non"}</span>
                </div>
                <div className="vd-detail__item">
                  <span className="vd-detail__label">Prochaines questions</span>
                  <span>{vox.UpcomingSchedules}</span>
                </div>
                <div className="vd-detail__item">
                  <span className="vd-detail__label">First use</span>
                  <span>{vox.FirstUseDate || "—"}</span>
                </div>
                <div className="vd-detail__item">
                  <span className="vd-detail__label">Jour / Nuit</span>
                  <span>{vox.IsNight ? "🌙" : "☀️"}</span>
                </div>
              </div>
              {vox.CurrentQuestion && (
                <div className="vd-detail__question">
                  <span className="vd-detail__label">Question en cours :</span> {vox.CurrentQuestion}
                </div>
              )}
              {vox.Errors.length > 0 && (
                <div className="vd-detail__errors">
                  {vox.Errors.map((e, i) => (
                    <div key={i} className={`vd-detail__error vd-detail__error--${e.severity}`}>
                      <strong>{e.type}</strong> — {e.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Mobile card ────────────────────────────────────────────────────────────

function VoxCard({ vox }: { vox: Vox }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`vd-card vd-card--${vox.State.toLowerCase()}`} onClick={() => setExpanded(!expanded)}>
      <div className="vd-card__header">
        <div>
          <span className="vd-name">{vox.Name}</span>
          <span className="vd-company"> · {vox.CompanyName}</span>
        </div>
        <StateBadge state={vox.State} />
      </div>
      <div className="vd-card__meta">
        {vox.LocationName && <span className="vd-muted">{vox.LocationName}</span>}
        <span className={vox.MinutesSinceLastAlive > 60 ? "vd-alert" : "vd-muted"}>
          {formatMinutes(vox.MinutesSinceLastAlive)} ago
        </span>
        <BatteryIcon pct={vox.Battery} charging={vox.IsCharging} />
        <span className={`vd-screen vd-screen--${vox.Screen}`}>{vox.Screen}</span>
      </div>
      {vox.Errors.length > 0 && (
        <div className="vd-card__errors">
          {vox.Errors.map((e, i) => (
            <span key={i} className={`vd-errtag vd-errtag--${e.severity}`}>{e.message}</span>
          ))}
        </div>
      )}
      {expanded && (
        <div className="vd-detail vd-detail--mobile">
          <div className="vd-detail__grid">
            <div className="vd-detail__item">
              <span className="vd-detail__label">Temp</span>
              <span>{vox.Temperature !== null ? `${vox.Temperature}°C` : "—"}</span>
            </div>
            <div className="vd-detail__item">
              <span className="vd-detail__label">Luminosité</span>
              <span>{vox.BrightnessPercent !== null ? `${vox.BrightnessPercent}%` : "—"}</span>
            </div>
            <div className="vd-detail__item">
              <span className="vd-detail__label">RAM</span>
              <span>{vox.MemoryMB !== null ? `${vox.MemoryMB} MB` : "—"}</span>
            </div>
            <div className="vd-detail__item">
              <span className="vd-detail__label">Version</span>
              <span>{vox.Version || "—"}</span>
            </div>
            <div className="vd-detail__item">
              <span className="vd-detail__label">First use</span>
              <span>{vox.FirstUseDate || "—"}</span>
            </div>
            <div className="vd-detail__item">
              <span className="vd-detail__label">Jour / Nuit</span>
              <span>{vox.IsNight ? "🌙" : "☀️"}</span>
            </div>
          </div>
          {vox.CurrentQuestion && (
            <div className="vd-detail__question">
              <span className="vd-detail__label">Question :</span> {vox.CurrentQuestion}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

export function ShowAllVoxes() {
  const [voxes, setVoxes] = useState<Vox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterState, setFilterState] = useState<VoxState | "ALL">("ALL");
  const [filterCompany, setFilterCompany] = useState<string>("ALL");
  const [search, setSearch] = useState("");

  useEffect(() => {
    serverService.getVoxDashboard().then(
      (data) => {
        if (data && data.status === "1") {
          const sorted = [...data.data].sort(
            (a, b) => STATE_ORDER[a.State as VoxState] - STATE_ORDER[b.State as VoxState]
          );
          setVoxes(sorted);
        } else {
          setError(data?.message || "Erreur API");
        }
        setLoading(false);
      },
      () => {
        setError("Erreur réseau — vérifiez l'API");
        setLoading(false);
      }
    );
  }, []);

  const companies = Array.from(new Set(voxes.map((v) => v.CompanyName))).sort();

  const counts: Record<string, number> = { ALL: voxes.length };
  voxes.forEach((v) => { counts[v.State] = (counts[v.State] || 0) + 1; });

  const filtered = voxes.filter((v) => {
    if (filterState !== "ALL" && v.State !== filterState) return false;
    if (filterCompany !== "ALL" && v.CompanyName !== filterCompany) return false;
    if (search &&
      !v.Name.toLowerCase().includes(search.toLowerCase()) &&
      !v.CompanyName.toLowerCase().includes(search.toLowerCase()) &&
      !(v.LocationName || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stateFilters: Array<VoxState | "ALL"> = ["ALL", "KO", "DEGRADED", "WARNING", "OK", "INACTIVE"];

  return (
    <div className="vd-dashboard">
      <header className="vd-header">
        <div className="vd-header__left">
          <span className="vd-dot" />
          <span className="vd-header__title">VOX<span className="vd-header__thin">MONITOR</span></span>
        </div>
        <div className="vd-header__stats">
          {(["KO", "DEGRADED", "WARNING"] as VoxState[]).filter(s => counts[s] > 0).map(s => (
            <span key={s} className={`vd-stat vd-stat--${s.toLowerCase()}`}>
              {counts[s]} {s}
            </span>
          ))}
          <span className="vd-stat vd-stat--total">{counts.ALL} total</span>
        </div>
      </header>

      <div className="vd-toolbar">
        <div className="vd-filters">
          {stateFilters.map((s) => (
            <button
              key={s}
              className={`vd-filter-btn vd-filter-btn--${s.toLowerCase()} ${filterState === s ? "vd-filter-btn--active" : ""}`}
              onClick={() => setFilterState(s)}
            >
              {s} ({counts[s] ?? 0})
            </button>
          ))}
        </div>

        <div className="vd-toolbar__right">
          <select
            className="vd-select"
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
          >
            <option value="ALL">Toutes les entreprises</option>
            {companies.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          <input
            type="text"
            className="vd-search"
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading && (
        <div className="vd-loading">
          <div className="vd-spinner" />
          <span>Chargement des Vox…</span>
        </div>
      )}

      {error && <div className="vd-error-banner">⚠ {error}</div>}

      {!loading && !error && (
        <>
          <div className="vd-table-container desktop-only">
            <table className="vd-table">
              <thead>
                <tr>
                  <th>État</th>
                  <th>Vox / Société</th>
                  <th>Location</th>
                  <th>Dernière vue</th>
                  <th>Batterie</th>
                  <th>Écran</th>
                  <th>Version</th>
                  <th>Erreurs</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={9} className="vd-empty">Aucun Vox ne correspond aux filtres</td></tr>
                ) : (
                  filtered.map((v) => <VoxRow key={v.IdVox} vox={v} />)
                )}
              </tbody>
            </table>
          </div>

          <div className="vd-cards mobile-only">
            {filtered.length === 0 ? (
              <div className="vd-empty">Aucun Vox ne correspond aux filtres</div>
            ) : (
              filtered.map((v) => <VoxCard key={v.IdVox} vox={v} />)
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ShowAllVoxes;